import moment, { Moment } from "moment";
import * as React from "react";
import { useContext, useEffect, useState, useCallback, RefObject } from "react";
import { AppContext } from "../../context/AppContext";
import Chart from "./index";
import DiaChartMapperInfluxClientData, { ApexOptions } from "./mapper/DiaChartMapperInfluxClientData";

/**
 * ChartClientDataProps
 */
interface ChartClientDataProps {
  from: string;
  to: string;
  resolution?: string
  onSetValues?: (from: string, to: string) => void,
  format?: string
}

/**
 * ChartClientData
 * @param from
 * @param to
 * @param resolution
 * @param onSetValues
 * @param format
 * @constructor
 */
/** */
export default function ChartClientData(
  {
    from,
    to,
    resolution,
    onSetValues = (from: string, to: string) => {},
    format = "DD.MM.YYYY"
  }: ChartClientDataProps
){
  // the default range is deliberately just today: at production data volume a
  // wider window delivers enough points to lock the renderer while apex builds
  // the chart -- users widen the range on purpose, the first paint stays light
  from = from || moment().format(format);
  to = to || moment().format(format);

  /**
   * elementRef
   */
  const elementRef: RefObject<HTMLElement> = React.createRef();

  /**
   * AppContext
   */
  const {
    I18N,
    services: {
      serviceInflux
    }
  } = useContext(AppContext);

  /**
   * series state
   */
  const [ series, setSeries ] = useState<ApexOptions['series']>();

  /**
   * request state: loading shows a visible indicator, error a panel with
   * retry -- without it the chart area just stayed blank
   */
  const [ status, setStatus ] = useState<'loading' | 'ready' | 'error'>('loading');

  /**
   * apex chart options
   */
  const options: ApexOptions = {
    xaxis: {
      title: {
        text: I18N.t("Datum/Uhrzeit")
      },
      type: "datetime"
    },
    yaxis: {
      title: {
        text: I18N.t("Wert")
      },
      type: "numeric",
      decimalsInFloat: 2
    },
    markers: {
      size: 5
    },
    chart: {
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      fontFamily: 'Inter, Arial, sans-serif',
      zoom: {
        enabled: false
      },
      toolbar: {
        tools: {
          download: '<button data-secondary-light data-icon-only data-small title="'+I18N.t("Download als SVG, PNG, CSV")+'"><i data-icon-before="download"></i></button>',
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    noData: {
      // apex paints this whenever the series is empty -- also during the
      // first load, where the loading line below already says what happens,
      // so the text only appears once the fetch settled without data
      text: status === 'loading' ? "" : I18N.t('Keine Daten verfügbar')
    }
  };

  /**
   * loadClientData
   */
  const loadClientData = useCallback(async () => {
    /**
     * check for empty values
     */
    if (from?.length === 0 || to?.length === 0 || resolution?.length === 0) return;

    // keep local copy of resolution for this invocation
    let currentResolution = resolution;

    /**
     * parse date by format to send Date Object to API Call
     */
    let parseFrom: Moment = moment(from, format),
      parseTo: Moment = moment(to, format),
      parseFromDate: Date,
      parseToDate: Date;

    parseFrom = !parseFrom.isValid() ? moment(from, format) : parseFrom;
    parseTo = !parseTo.isValid() ? moment(to, format) : parseTo;

    parseFromDate = parseFrom.toDate();
    parseToDate = parseTo.toDate();

    /**
     * if from and to date exact the same we create a diff of ca. 24 hours
     */
    if (parseFromDate.toISOString() === parseToDate.toISOString()) {
      parseFromDate.setHours(0);
      parseFromDate.setMinutes(0);
      parseFromDate.setSeconds(0);

      parseToDate.setHours(23);
      parseToDate.setMinutes(59);
      parseToDate.setSeconds(59);
    }

    /**
     * calculate resolution based on date range and element dimension
     */
    currentResolution = calculateInterval(
      parseFromDate.getTime(),
      parseToDate.getTime(),
      (elementRef.current?.offsetWidth || 1200) / 3
    );

    /**
     * invoke callback to post values to parent listener if it exists
     */
    if (from && to) {
      onSetValues(from, to);
    }

    /**
     * get client data from service
     */
    // a failed fetch used to die as an unhandled rejection and the panel
    // stayed blank forever; now it surfaces as a visible error state
    try {
      setStatus('loading');

      const clientData: any = await serviceInflux.clientData(parseFromDate, parseToDate, currentResolution);

      /**
       * map influx data to apex chart format
       */
      let diaChartMapperClientData: DiaChartMapperInfluxClientData = new DiaChartMapperInfluxClientData(clientData);

      /**
       * update series state with mapped data
       */
      setSeries(diaChartMapperClientData.getSeries());
      setStatus('ready');
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setStatus('error');
    }
  // eslint-disable-next-line
  }, [from, to, resolution]);

  /**
   * get client data from api and map influx data to apex format by mapper
   */
  useEffect(() => {
    loadClientData();
  }, [ loadClientData ]);

  /**
   * calculate interval for date range
   *
   * @param {number} start
   * @param {number} end
   * @param {number} targetBuckets
   * @param {number} adjustRatio
   * @returns {string} "10s", "5m", "1h"
   */
  const calculateInterval = (
    start: number,
    end: number,
    targetBuckets: number,
    adjustRatio: number = 10
  ): string => {
    // Berechne die Gesamtdauer in Sekunden
    const durationSeconds = (end - start) / 1000;

    // Idealer Intervall (in Sekunden), um etwa targetBuckets Buckets zu erhalten
    const idealInterval = durationSeconds / (targetBuckets / adjustRatio);

    // Vordefinierte „schöne“ Intervalle (in Sekunden)
    const intervals = [1, 5, 10, 15, 30, 60, 300, 600, 900, 1800, 3600];

    // Suche das erste Intervall, das größer oder gleich dem idealen Intervall ist
    let chosenInterval = intervals.find(interval => interval >= idealInterval);

    // Falls idealInterval größer ist als alle vordefinierten, runde auf ein Vielfaches von 3600 (Stunden) auf
    if (chosenInterval === undefined) {
      chosenInterval = Math.ceil(idealInterval / 3600) * 3600;
    }

    // Wandle das Intervall in einen String mit passender Einheit um
    if (chosenInterval < 60) {
      return chosenInterval + "s";
    } else if (chosenInterval < 3600) {
      return (chosenInterval / 60) + "m";
    } else {
      return (chosenInterval / 3600) + "h";
    }
  }

  /**
   * render client data chart with series data
   */
  if (status === 'error') {
    return (
      <div data-chart-error role="alert">
        <p>{ I18N.t("Die Daten konnten nicht geladen werden.") }</p>
        <button
          data-secondary
          data-small
          onClick={ () => loadClientData() }
        >
          { I18N.t("Erneut versuchen") }
        </button>
      </div>
    );
  }

  return (
    <>
      { /* the live region exists from the first render on and only its text
           toggles: a region inserted together with its content is usually
           not announced at all */ }
      <p data-chart-loading aria-live="polite">
        { status === 'loading' ? I18N.t("Daten werden geladen …") : "" }
      </p>
      <Chart
        // @ts-ignore
        ref={ elementRef }
        type="line"
        series={ series }
        options={ options }
      ></Chart>
    </>
  );
}
